/**
 * Search overlay component
 * Displays a centered modal with search input, results, and menu navigation.
 * When results are present they replace the menu columns/footer.
 */

import { useState, useEffect, useRef } from "react";
import type { Menu, Topbar, LocalizedTexts } from "@/types/layout";
import { SearchIcon } from "@/components/icons";
import { useSearch } from "@/hooks/useSearch";
import { getItemName, getItemLink, getItemImage } from "@/types/search";
import { resolveHref } from "@/utils/url";
import { MenuColumnsAndFooter } from "./MenuContentCard";
import styles from "./Header.module.css";

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  menu: Menu;
  topbar: Topbar;
  currentLocale: string;
  localizedTexts: LocalizedTexts;
  searchPlaceholder?: string;
  /**
   * The term the overlay opens with — a search another app handed over. The
   * field starts with it typed in and the results already on their way.
   */
  initialQuery?: string;
}

export function SearchOverlay({
  isOpen,
  onClose,
  menu,
  topbar,
  currentLocale,
  localizedTexts,
  searchPlaceholder = "Search for...",
  initialQuery,
}: SearchOverlayProps) {
  const [query, setQuery] = useState(initialQuery ?? "");
  const { results, isLoading } = useSearch(query);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Only a hand-over pre-fills; opening from the icon starts blank as
      // before, and the close below already cleared the last term.
      if (initialQuery) setQuery(initialQuery);
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
    setQuery("");
  }, [isOpen, initialQuery]);

  // Only items we can actually navigate to are worth a row. Everything the CMS can return without a
  // routable URL (unroutable pages, containers) would otherwise render as a link to the locale home.
  const linkableItems = (results?.items ?? []).flatMap((item) => {
    const link = getItemLink(item);
    return link ? [{ item, href: resolveHref(link, currentLocale) }] : [];
  });

  const hasResults = linkableItems.length > 0;
  const hasEmptyResults =
    results !== null && !hasResults && query.trim().length > 0;

  return (
    <div
      className={`${styles.searchPanel} ${isOpen ? styles.searchPanelOpen : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <div className={styles.menuContent}>
        {/* Search input */}
        <div className={styles.searchInputWrapper}>
          <SearchIcon className={styles.searchInputIcon} />
          <input
            ref={inputRef}
            type="search"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            className={styles.searchCloseBtn}
            onClick={onClose}
            aria-label="Close search"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Loading state */}
        {isLoading && <div className={styles.searchLoading}>Searching...</div>}

        {/* Results replace columns/footer when present */}
        {hasResults ? (
          <div className={styles.searchResults}>
            {linkableItems.map(({ item, href }) => {
              const name = getItemName(item);
              const image = getItemImage(item);

              return (
                <a
                  key={item.id}
                  href={href}
                  className={styles.searchResultItem}
                  onClick={onClose}
                >
                  {image && (
                    <img
                      src={image.url}
                      alt={image.altText ?? name}
                      className={styles.searchResultImage}
                    />
                  )}
                  <span className={styles.searchResultName}>{name}</span>
                </a>
              );
            })}
          </div>
        ) : hasEmptyResults && !isLoading ? (
          <div className={styles.searchEmpty}>No results found</div>
        ) : (
          <MenuColumnsAndFooter
            menu={menu}
            topbar={topbar}
            currentLocale={currentLocale}
            localizedTexts={localizedTexts}
            onLinkClick={onClose}
            showImage={false}
          />
        )}
      </div>
    </div>
  );
}

export default SearchOverlay;
